import React, { useState, useEffect, useMemo, useRef } from "react";
import { fetchCurrencyList, getExchangeRate } from "../../api/nbgCurrencyApi";
import SearchableSelect from "../SearchableSelect";

const LANGUAGES = [
  { value: "geo", label: "ქართული / GEO" },
  { value: "eng", label: "English / ENG" },
  { value: "rus", label: "Русский / RUS" },
];

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "on_hold", label: "On Hold" },
  { value: "cancelled", label: "Cancelled" },
];

const makeInitialState = (value) => ({
  id: value?.id ?? null,
  name: value?.name ?? "",
  location: value?.location ?? "",
  customer_id: value?.customer_id ? String(value.customer_id) : "",
  date: value?.date ?? new Date().toISOString().split("T")[0],
  language: value?.language ?? "geo",
  real_currency: value?.real_currency ?? "GEL",
  additional_currency: value?.additional_currency ?? "GEL",
  course: value?.course !== undefined && value?.course !== null ? String(value.course) : "1",
  building_type: value?.building_type ?? "",
  area: value?.area !== undefined && value?.area !== null ? String(value.area) : "",
  status: value?.status ?? "new",
  description: value?.description ?? "",
  members: value?.members ?? value?.memberships ?? [],
});

const formatRate = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "1.0000";
  return num.toFixed(4);
};

export default function MainInfoTab({
  project,
  onSave,
  onDelete,
  onDuplicate,
  isAdmin,
  isSuperuser = false,
  saving,
  customers = [],
  mode = "edit",
}) {
  const [form, setForm] = useState(() => makeInitialState(project));
  const [currencies, setCurrencies] = useState(["GEL", "USD", "EUR", "RUB", "GBP"]);
  const [resettingRate, setResettingRate] = useState(false);
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  // Skip the auto exchange-rate fetch on mount / project reload so a manually
  // saved course is not silently overwritten with the live rate.
  const skipRateFetchRef = useRef(true);

  useEffect(() => {
    setForm(makeInitialState(project));
    skipRateFetchRef.current = true;
  }, [project]);

  useEffect(() => {
    fetchCurrencyList().then(setCurrencies).catch(() => {});
  }, []);

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        id: customer.id,
        name: customer.name,
      })),
    [customers]
  );

  const canEdit = isAdmin || mode === "create";

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Auto-update exchange rate whenever either currency changes
  useEffect(() => {
    let cancelled = false;
    const from = (form.real_currency || "").toUpperCase();
    const to = (form.additional_currency || "").toUpperCase();
    if (!from || !to) return;

    if (skipRateFetchRef.current) {
      skipRateFetchRef.current = false;
      return;
    }

    if (from === to) {
      setForm((prev) => (prev.course === "1.0000" ? prev : { ...prev, course: "1.0000" }));
      return;
    }

    (async () => {
      try {
        const rate = await getExchangeRate(from, to);
        if (!cancelled) {
          setForm((prev) => ({ ...prev, course: formatRate(rate) }));
        }
      } catch {
        if (!cancelled) {
          setForm((prev) => ({ ...prev, course: "1.0000" }));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [form.real_currency, form.additional_currency]);

  const handleResetRate = async () => {
    if (!canEdit) return;
    setResettingRate(true);
    try {
      const rate = await getExchangeRate(form.real_currency, form.additional_currency);
      setForm((prev) => ({ ...prev, course: formatRate(rate) }));
    } catch {
      setForm((prev) => ({ ...prev, course: "1.0000" }));
    } finally {
      setResettingRate(false);
    }
  };

  const handleCourseBlur = () => {
    setForm((prev) => ({ ...prev, course: formatRate(prev.course) }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canEdit) return;
    onSave(form);
  };

  const handleStartDuplicate = () => {
    setDuplicateName(`${project?.name || ""} (Copy)`);
    setDuplicateError("");
    setShowDuplicateForm(true);
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateForm(false);
    setDuplicateError("");
  };

  const handleConfirmDuplicate = async () => {
    if (typeof onDuplicate !== "function" || !project?.id) return;
    setDuplicating(true);
    setDuplicateError("");
    try {
      await onDuplicate(project.id, duplicateName.trim());
      setShowDuplicateForm(false);
    } catch (err) {
      setDuplicateError(err?.message || "Unable to duplicate project.");
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <form className="project-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <label htmlFor="project-name">Project name</label>
        <input
          id="project-name"
          value={form.name}
          onChange={handleChange("name")}
          placeholder="New project"
          required
          disabled={!canEdit}
        />
      </div>

      <div className="form-section">
        <label htmlFor="project-customer">Customer</label>
        <SearchableSelect
          id="project-customer"
          value={form.customer_id}
          onChange={(val) => setForm((prev) => ({ ...prev, customer_id: String(val) }))}
          options={customerOptions.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Type to search customer..."
          disabled={!canEdit || customerOptions.length === 0}
          required
        />
      </div>

      <div className="form-row">
        <div className="form-section">
          <label htmlFor="project-date">Project date</label>
          <input
            id="project-date"
            type="date"
            value={form.date}
            onChange={handleChange("date")}
            required
            disabled={!canEdit}
          />
        </div>

        <div className="form-section">
          <label htmlFor="project-location">Location</label>
          <input
            id="project-location"
            value={form.location}
            onChange={handleChange("location")}
            placeholder="City"
            required
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-section">
          <label htmlFor="project-language">Language</label>
          <select
            id="project-language"
            value={form.language}
            onChange={handleChange("language")}
            required
            disabled={!canEdit}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-section">
          <label htmlFor="project-status">Status</label>
          <select
            id="project-status"
            value={form.status}
            onChange={handleChange("status")}
            required
            disabled={!canEdit}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-section">
          <label htmlFor="project-real-currency">Primary currency</label>
          <select
            id="project-real-currency"
            value={form.real_currency}
            onChange={handleChange("real_currency")}
            required
            disabled={!canEdit}
          >
            {currencies.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>

        <div className="form-section">
          <label htmlFor="project-additional-currency">Secondary currency</label>
          <select
            id="project-additional-currency"
            value={form.additional_currency}
            onChange={handleChange("additional_currency")}
            required
            disabled={!canEdit}
          >
            {currencies.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-section">
          <label htmlFor="project-course">Exchange rate</label>
          <div className="input-with-action">
            <input
              id="project-course"
              type="number"
              step="0.0001"
              min="0"
              value={form.course}
              onChange={handleChange("course")}
              onBlur={handleCourseBlur}
              required
              disabled={!canEdit}
            />
            <button
              type="button"
              className="reset-btn"
              onClick={handleResetRate}
              disabled={!canEdit || resettingRate}
              title="Reset rate from nbg.gov.ge"
            >
              {resettingRate ? "…" : "↻ Reset"}
            </button>
          </div>
        </div>

        <div className="form-section">
          <label htmlFor="project-area">Area (m²)</label>
          <input
            id="project-area"
            type="number"
            min="0"
            value={form.area}
            onChange={handleChange("area")}
            required
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="form-section">
        <label htmlFor="project-building-type">Building type</label>
        <input
          id="project-building-type"
          value={form.building_type}
          onChange={handleChange("building_type")}
          placeholder="Office"
          required
          disabled={!canEdit}
        />
      </div>

      <div className="form-section">
        <label htmlFor="project-description">
          Description <span className="label-optional">(optional)</span>
        </label>
        <textarea
          id="project-description"
          value={form.description}
          onChange={handleChange("description")}
          placeholder="Project details"
          disabled={!canEdit}
        />
      </div>

      <div className="form-actions">
        <button type="submit" className="primary" disabled={!canEdit || saving}>
          {saving ? "Saving…" : mode === "create" ? "Create project" : "Save changes"}
        </button>

        {mode !== "create" && typeof onDuplicate === "function" && !showDuplicateForm && (
          <button type="button" className="secondary" onClick={handleStartDuplicate} disabled={saving}>
            Duplicate project
          </button>
        )}

        {mode !== "create" && isSuperuser && typeof onDelete === "function" && (
          <button type="button" className="danger" onClick={onDelete} disabled={saving}>
            Delete project
          </button>
        )}
      </div>

      {showDuplicateForm && (
        <div className="duplicate-project-panel">
          <label htmlFor="duplicate-project-name">
            New project name
            <span className="label-optional">clones settings, BOQ tree &amp; child sheets</span>
          </label>
          <div className="duplicate-project-row">
            <input
              id="duplicate-project-name"
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              disabled={duplicating}
              autoFocus
            />
            <button
              type="button"
              className="primary"
              onClick={handleConfirmDuplicate}
              disabled={duplicating || !duplicateName.trim()}
            >
              {duplicating ? "Duplicating…" : "Create duplicate"}
            </button>
            <button type="button" className="secondary" onClick={handleCancelDuplicate} disabled={duplicating}>
              Cancel
            </button>
          </div>
          {duplicateError && <div className="msg-error">{duplicateError}</div>}
        </div>
      )}
    </form>
  );
}
